import React, { FC, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Checkbox, FormControlLabel } from '@mui/material';
import OvCompactButton from '../atoms/OvCompactButton';
import OvTextField from '../atoms/OvTextField';
import OvStagedPlacementSummary from './OvStagedPlacementSummary';
import OvVideoHealthGoalsField from './OvVideoHealthGoalsField';
import OvVideoPlacementPicker from './OvVideoPlacementPicker';
import { NO_PLACEMENT } from '../../../common/model/dto/video/video-constants';
import { StagedVideo } from '../../../common/model/type/video-file.types';
import { ProposedAssignment } from '../../../common/model/type/video-staging.types';
import { isValidPosition } from '../../../common/utils/video-placement-utils';
import { proposeBatchAssignment } from '../../../common/utils/video-staging-utils';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';

interface OvVideoBatchAssignmentProps {
  selected: StagedVideo[];
  onApply: (proposals: ProposedAssignment[]) => void;
  onCancel: () => void;
}

const OvVideoBatchAssignment: FC<OvVideoBatchAssignmentProps> = ({
  selected,
  onApply,
  onCancel,
}) => {
  const { t } = useTranslation();
  const id = useId();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [isPlacementOn, setIsPlacementOn] = useState(false);
  const [isOrderOn, setIsOrderOn] = useState(false);
  const [isGoalsOn, setIsGoalsOn] = useState(false);
  const [placementKey, setPlacementKey] = useState(NO_PLACEMENT);
  const [startOrder, setStartOrder] = useState('0');
  const [goals, setGoals] = useState<string[]>([]);

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const isOrderInvalid = isOrderOn && !isValidPosition(startOrder);
  const isNothingOn = !isPlacementOn && !isOrderOn && !isGoalsOn;
  const proposals = proposeBatchAssignment(selected, {
    placementKey: isPlacementOn ? placementKey : null,
    startOrder: isOrderOn && !isOrderInvalid ? startOrder : null,
    goals: isGoalsOn ? goals : null,
  });

  const getBlockReason = () => {
    if (isNothingOn) {
      return t('videos.bulk.batch.nothingEnabled');
    }

    return isOrderInvalid ? t('videos.placement.positionInvalid') : null;
  };

  const blockReason = getBlockReason();

  return (
    <Editor aria-labelledby={`${id}-heading`}>
      <Heading id={`${id}-heading`} ref={headingRef} tabIndex={-1}>
        {t('videos.bulk.applyToSelected')}
      </Heading>
      <Hint>{t('videos.bulk.batch.hint')}</Hint>

      <Field>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={isPlacementOn}
              onChange={(event) => setIsPlacementOn(event.target.checked)}
            />
          }
          label={t('videos.bulk.batch.enablePlacement')}
        />
        {isPlacementOn && (
          <OvVideoPlacementPicker
            id={`${id}-placement`}
            label={t('videos.fields.shownIn')}
            value={placementKey}
            onChange={setPlacementKey}
          />
        )}
      </Field>

      <Field>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={isOrderOn}
              onChange={(event) => setIsOrderOn(event.target.checked)}
            />
          }
          label={t('videos.bulk.batch.enableOrder')}
        />
        {isOrderOn && (
          <OvTextField
            id={`${id}-order`}
            size="small"
            label={t('videos.bulk.batch.startOrder')}
            value={startOrder}
            inputProps={{ inputMode: 'numeric' }}
            error={isOrderInvalid}
            helperText={
              isOrderInvalid
                ? t('videos.placement.positionInvalid')
                : t('videos.bulk.batch.startOrderHint')
            }
            onChange={(event) => setStartOrder(event.target.value)}
          />
        )}
      </Field>

      <Field>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={isGoalsOn}
              onChange={(event) => setIsGoalsOn(event.target.checked)}
            />
          }
          label={t('videos.bulk.batch.enableGoals')}
        />
        {isGoalsOn && (
          <OvVideoHealthGoalsField goals={goals} onChange={setGoals} />
        )}
      </Field>

      <Preview aria-label={t('videos.bulk.batch.previewLabel')}>
        {proposals.map(({ key, fileName, values }) => (
          <li key={key}>
            <FileName>{fileName}</FileName>{' '}
            <OvStagedPlacementSummary values={values} />
          </li>
        ))}
      </Preview>

      {blockReason && <Reason id={`${id}-reason`}>{blockReason}</Reason>}
      <Actions>
        <OvCompactButton tone="secondary" onClick={onCancel}>
          {t('common.actions.cancel')}
        </OvCompactButton>
        <OvCompactButton
          tone="primary"
          disabled={!!blockReason}
          aria-describedby={blockReason ? `${id}-reason` : undefined}
          onClick={() => onApply(proposals)}
        >
          {t('videos.bulk.batch.apply', { count: selected.length })}
        </OvCompactButton>
      </Actions>
    </Editor>
  );
};

export default OvVideoBatchAssignment;

const Editor = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${Variables.spacing.SMALL};
  padding: ${Variables.spacing.MEDIUM};
  border: 1px solid ${Colours.OV_BORDER_COLOR_V3};
  border-radius: ${Variables.borderRadius.LARGE};
`;

const Heading = styled.h2`
  margin: 0;
  font-size: ${Variables.fontSizes.MEDIUM};

  &:focus {
    outline: none;
  }
`;

const Hint = styled.p`
  margin: 0;
  font-size: ${Variables.fontSizes.SMALL};
  color: ${Colours.OV_CHARCOAL};
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-width: 32rem;
`;

const Preview = styled.ul`
  margin: 0;
  padding-left: 1.25rem;
  font-size: ${Variables.fontSizes.SMALL};
`;

const FileName = styled.span`
  font-weight: bold;
  overflow-wrap: anywhere;
`;

const Reason = styled.p`
  margin: 0;
  font-size: ${Variables.fontSizes.SMALL};
  color: ${Colours.OV_CHARCOAL};
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.75rem;
`;
